import React, { useEffect, useMemo, useRef, useState } from "react";
import { LuPause, LuPlay, LuSquare } from "react-icons/lu";
import { parseUtc } from "../../utils/datetime";

function formatDuration(ms) {
  if (!ms || ms < 0) return "0s";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

const STATUS_LABELS = {
  queued: "Queued...",
  running: "Searching...",
  paused: "Paused",
  stopped: "Stopped",
  completed: "Completed",
  failed: "Failed",
};

function SearchProgress({ search, onPause, onResume, onStop }) {
  const [now, setNow] = useState(Date.now());
  const timerRef = useRef(null);

  const isLive = search.status === "running" || search.status === "queued" || search.status === "paused";

  useEffect(() => {
    if (search.status === "running") {
      timerRef.current = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(timerRef.current);
    }
    return undefined;
  }, [search.status]);

  const { percent, elapsedMs, remainingMs } = useMemo(() => {
    const total = search.max_results > 0 ? search.max_results : 0;
    const found = search.total_found || 0;
    const pct = total > 0 ? Math.min(100, Math.round((found / total) * 100)) : (isLive ? 50 : 100);

    // parseUtc, not a bare Date: these are UTC DATETIME strings, and parsing
    // them as local time would skew elapsed/remaining by the viewer's offset.
    const startedAt = parseUtc(search.started_at)?.getTime() ?? null;
    const endRef = parseUtc(search.completed_at)?.getTime() ?? now;
    const elapsed = startedAt ? endRef - startedAt : 0;

    let remaining = 0;
    if (search.status === "running" && total > 0 && found > 0 && elapsed > 0) {
      const perLead = elapsed / found;
      remaining = perLead * (total - found);
    }
    return { percent: pct, elapsedMs: elapsed, remainingMs: remaining };
  }, [search, now, isLive]);

  const foundLabel = search.max_results > 0
    ? `${search.total_found || 0} / ${search.max_results}`
    : `${search.total_found || 0}`;

  return (
    <div className="lf-card">
      <div className="lf-progress-head">
        <span className="lf-progress-status">
          <span className={`lf-status-dot ${search.status}`} />
          {STATUS_LABELS[search.status] || search.status}
          <span className="lf-muted" style={{ fontWeight: 500, marginLeft: 6 }}>
            "{search.keyword}" in {search.location}
          </span>
        </span>

        {isLive && (
          <div className="lf-progress-actions">
            {search.status === "paused" ? (
              <button className="lf-btn lf-btn-ghost lf-btn-sm" onClick={onResume}>
                <LuPlay style={{ verticalAlign: "-2px" }} /> Resume
              </button>
            ) : (
              <button className="lf-btn lf-btn-ghost lf-btn-sm" onClick={onPause} disabled={search.status === "queued"}>
                <LuPause style={{ verticalAlign: "-2px" }} /> Pause
              </button>
            )}
            <button className="lf-btn lf-btn-danger lf-btn-sm" onClick={onStop}>
              <LuSquare style={{ verticalAlign: "-2px" }} /> Stop
            </button>
          </div>
        )}
      </div>

      {search.status === "failed" && search.error && (
        <div style={{
          background: "#ffe9e9", color: "#b32020", borderRadius: 9,
          padding: "10px 14px", fontSize: 13, marginBottom: 12, lineHeight: 1.4,
        }}>
          {search.error}
        </div>
      )}

      <div className="lf-progress-bar">
        <div className="lf-progress-fill" style={{ width: `${percent}%` }} />
      </div>

      <div className="lf-progress-meta">
        <div className="lf-meta-item">
          Collected
          <strong>{foundLabel} Leads</strong>
        </div>
        <div className="lf-meta-item">
          Time elapsed
          <strong>{formatDuration(elapsedMs)}</strong>
        </div>
        <div className="lf-meta-item">
          Estimated remaining
          <strong>{search.status === "running" ? formatDuration(remainingMs) : "—"}</strong>
        </div>
        <div className="lf-meta-item">
          Processed
          <strong>{search.processed || 0}</strong>
        </div>
      </div>
    </div>
  );
}

export default SearchProgress;
